import {
  Activity,
  AddFunds,
  Business,
  FindFile,
  Help,
  Home,
  Logout,
  Navbar,
  Send,
  Settings,
  Sparkles,
  Users,
  WithdrawFunds,
} from '@airtm/airtm-ui';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CUSTODIAL_PROVIDERS } from '@constants';
import useMe from 'utils/hooks/useMe/useMe';
import isUserVerified from '@airtm/utils/dist/utilities/isUserVerified';
import useLogout from './useLogout/useLogout';

type Props = {
  className: string;
  onItemClicked: () => void;
};

const NavbarComponent = function ({ className, onItemClicked }: Props) {
  const { t } = useTranslation(['LAYOUT', 'ENTERPRISE_LAYOUT', 'ENTERPRISE_AIRNER_INTELLIGENCE']);
  const { logout } = useLogout();
  const { me, custodialProvider } = useMe({
    include: { enterprise: true, securityHub: true, preferences: true },
    fetchPolicy: 'cache-first',
  });

  const {
    canAddFunds,
    canAccessWithdraw,
    canCreateSinglePayment,
    canCreateBulkPayment,
    canApproveBulkPayment,
    canCancelBulkPayment,
    canAccessAirners,
  } = me?.organizationPermissions ?? {};

  const showAddFundButton = canAddFunds && !!custodialProvider;
  const showWithdrawButton = custodialProvider === CUSTODIAL_PROVIDERS.BRIDGE && canAccessWithdraw;
  const showAirnersFeature = canAccessAirners && me?.enterprise?.activeAirnerFeature;

  const showPayoutButton = [
    canCreateSinglePayment,
    canCreateBulkPayment,
    canApproveBulkPayment,
    canCancelBulkPayment,
  ].some(Boolean);

  const buttons = [
    <Navbar.Link
      component={NavLink}
      data-testid="home-sidebar"
      icon={<Home />}
      key="home"
      to="/home"
      label={t('LAYOUT:NAVBAR_HOME')}
      onClick={onItemClicked}
    />,
    showAddFundButton && (
      <Navbar.Link
        component={NavLink}
        data-testid="fund-sidebar"
        icon={<AddFunds />}
        key="fund"
        to="/fund"
        label={t('LAYOUT:NAVBAR_FUND')}
        onClick={onItemClicked}
      />
    ),
    showWithdrawButton && (
      <Navbar.Link
        component={NavLink}
        data-testid="withdraw-sidebar"
        icon={<WithdrawFunds />}
        key="withdraw"
        to="/withdraw"
        label={t('LAYOUT:NAVBAR_WITHDRAW')}
        onClick={onItemClicked}
      />
    ),
    showPayoutButton && (
      <Navbar.Link
        component={NavLink}
        data-testid="payout-sidebar"
        icon={<Send />}
        key="payout"
        to="/payout"
        label={t('LAYOUT:NAVBAR_PAYOUT')}
        onClick={onItemClicked}
      />
    ),
    <Navbar.Link
      component={NavLink}
      data-testid="activity-sidebar"
      icon={<Activity />}
      key="activity"
      to="/activity/payouts"
      label={t('LAYOUT:NAVBAR_ACTIVITY')}
      onClick={onItemClicked}
    />,
    showAirnersFeature && (
      <Navbar.Link
        data-testid="airner-intelligence"
        key="airner-intelligence"
        icon={<Sparkles />}
        label={t('ENTERPRISE_LAYOUT:NAVBAR_AIRNER_INTELLIGENCE')}
      >
        <Navbar.Link
          component={NavLink}
          key="airner-intelligence-find-talent"
          to="/airner-intelligence/find-talent"
          label={t('ENTERPRISE_AIRNER_INTELLIGENCE:AIRNER_INTELLIGENCE_FIND_TALENT_SECTION')}
          data-testid="airner-intelligence-find-talent"
          onClick={onItemClicked}
          icon={<FindFile />}
        />
        <Navbar.Link
          component={NavLink}
          key="airner-intelligence-your-users"
          to="/airner-intelligence/your-users"
          label={t('ENTERPRISE_LAYOUT:NAVBAR_AIRNER_INTELLIGENCE_YOUR_USERS')}
          data-testid="airner-intelligence-your-users"
          onClick={onItemClicked}
          icon={<Users />}
        />
        <Navbar.Link
          component={NavLink}
          key="airner-intelligence-your-jobs"
          to="/airner-intelligence/your-jobs"
          label={t('ENTERPRISE_LAYOUT:NAVBAR_AIRNER_INTELLIGENCE_YOUR_JOBS')}
          data-testid="airner-intelligence-your-jobs"
          onClick={onItemClicked}
          icon={<Business />}
        />
      </Navbar.Link>
    ),
  ];

  const bottomButtons = [
    <Navbar.Link
      component={NavLink}
      data-testid="settings"
      icon={<Settings />}
      key="settings"
      to="/settings"
      label={t('LAYOUT:NAVBAR_SETTINGS')}
      onClick={onItemClicked}
    />,
    <Navbar.Link
      data-testid="help-sidebar"
      icon={<Help />}
      key="help"
      href="https://help.airtm.com/en/support/solutions/47000525127"
      label={t('LAYOUT:NAVBAR_HELP')}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onItemClicked}
    />,
    <Navbar.Delimiter key="delimiter" />,
    <Navbar.Link
      data-testid="logout"
      key="logout"
      icon={<Logout />}
      label={t('LAYOUT:NAVBAR_LOGOUT')}
      onClick={() => {
        logout();
        onItemClicked();
      }}
    />,
  ];

  return (
    <Navbar
      className={className}
      showEnterpriseLogo
      user={{
        firstName: me?.firstName ?? '',
        lastName: me?.lastName ?? '',
        preferences: me?.preferences,
        numbers: {
          score: null,
        },
        verified: isUserVerified(me?.securityHub),
      }}
      navbarLinksBottom={bottomButtons}
    >
      {buttons}
    </Navbar>
  );
};

export default NavbarComponent;
